<template>
  <section 
    class="hero-section" 
    :style="backgroundStyles"
    role="banner"
    aria-labelledby="hero-title"
  >
    <div class="hero-section__container">
      <div class="hero-section__content">
        <!-- Loading State -->
        <div v-if="isLoading" class="hero-section__loading">
          <div class="hero-section__spinner" role="status" aria-label="Loading hero content"></div>
          <p class="sr-only">{{ $t('labels.loading', 'Loading content...') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="hasError" class="hero-section__error">
          <p>{{ $t('errors.contentUnavailable', 'Content temporarily unavailable') }}</p>
        </div>

        <!-- Hero Content -->
        <div v-else class="hero-section__inner">
          <!-- Hero Title - Image or Text -->
          <div class="hero-section__title-wrapper">
            <img 
              v-if="heroData.titleImage"
              :src="heroData.titleImage"
              :alt="heroData.titleImageAlt || 'Game Title'"
              class="hero-section__title-image"
              id="hero-title"
            />
            <h1 
              v-else
              id="hero-title" 
              class="hero-section__title"
              v-html="heroData.title"
            ></h1>
          </div>
          
          <p 
            v-if="heroData.subtitle" 
            class="hero-section__subtitle"
            v-html="heroData.subtitle"
          ></p>
          
          <div 
            v-if="heroData.description" 
            class="hero-section__description"
            v-html="heroData.description"
          ></div>

          <!-- Call to Action Buttons -->
          <div class="hero-section__actions">
            <a 
              v-if="heroData.steamUrl"
              :href="heroData.steamUrl"
              class="steam-button"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="heroData.wishlistButtonText || $t('actions.wishlistOnSteam', 'Wishlist on Steam')"
              @click="trackSteamClick"
            >
              {{ heroData.wishlistButtonText || $t('actions.wishlistOnSteam', 'Wishlist on Steam') }}
            </a>

            <a 
              v-if="heroData.secondaryButtonUrl && heroData.secondaryButtonText"
              :href="heroData.secondaryButtonUrl"
              class="btn btn--secondary hero-section__secondary-btn"
              :target="heroData.secondaryButtonUrl.startsWith('http') ? '_blank' : '_self'"
              :rel="heroData.secondaryButtonUrl.startsWith('http') ? 'noopener noreferrer' : null"
              @click="trackSupportClick"
            >
              {{ heroData.secondaryButtonText }}
            </a>
          </div>

          <!-- Features/Key Points -->
          <div v-if="heroData.features && heroData.features.length" class="hero-section__features">
            <ul class="hero-features">
              <li 
                v-for="(feature, index) in heroData.features" 
                :key="index"
                class="hero-features__item"
              >
                <span class="hero-features__icon" aria-hidden="true">✓</span>
                <span class="hero-features__text">{{ feature }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Background Video (optional) -->
      <div 
        v-if="heroData.backgroundVideo && !prefersReducedMotion" 
        class="hero-section__video"
      >
        <video 
          ref="backgroundVideo"
          class="hero-section__video-element"
          :src="heroData.backgroundVideo"
          autoplay
          muted
          loop
          playsinline
          :aria-label="$t('labels.backgroundVideo', 'Background video')"
        ></video>
      </div>

      <!-- Scroll Indicator -->
      <div class="hero-section__scroll-indicator" v-if="showScrollIndicator">
        <button 
          @click="scrollToContent"
          class="scroll-indicator"
          :aria-label="$t('actions.scrollToContent', 'Scroll to main content')"
        >
          <svg 
            class="scroll-indicator__icon" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
          </svg>
          <span class="scroll-indicator__text">{{ $t('actions.scrollDown', 'Scroll') }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import useHero from './Hero'

// Accept optional content from parent (e.g., HomePage passes data.hero)
const props = defineProps({
  content: { type: Object, default: null }
})

const {
  // state
  isLoading,
  hasError,
  heroData,
  backgroundStyles,
  prefersReducedMotion,
  backgroundVideo,
  showScrollIndicator,
  
  // methods
  fetchHeroData,
  trackSteamClick,
  trackSupportClick,
  scrollToContent,
  
  // i18n
  $t
} = useHero(props.content)

// Expose methods for parent components if needed
defineExpose({
  fetchHeroData,
  scrollToContent
})
</script>

<style lang="scss" scoped>
@use './Hero.scss';
</style>
